import React, { useState, useEffect } from 'react';
import { isNil } from 'lodash';
import { ResponsiveContainer, PieChart as RePieChart, Pie, Legend } from 'recharts';
import { Empty } from 'antd';
import { Formatter } from '../../../utils';
import { COLORS, normalizeNameFields } from './graphUtils';
import getCustomTooltip from './CustomTooltip';
import './styles.scoped.css';

const Tooltip = getCustomTooltip();

function normalizeData(data: any, totalCount: number, countKey: string, fieldKey: string) {
  if (!data?.length) return [];

  data = normalizeNameFields(data, fieldKey);

  const pieData = data.map((d: any, i: number) => ({
    name: String(d[fieldKey]),
    count: Number(Number(d[countKey])?.toFixed(2)),
    fill: COLORS.Pie[i % COLORS.Pie.length],
  }));

  const topTotal = pieData.reduce((prevSum: number, currVal: any) => prevSum + currVal.count, 0);
  const diff = totalCount - topTotal;

  // Show empty grey circle when total is 0
  if (!totalCount) {
    pieData.push({
      count: 100,
      fill: COLORS.Empty,
      stroke: COLORS.Empty,
    });
  }
  else if (diff) {
    pieData.push({
      name: 'Others',
      count: Number(diff.toFixed(2)),
      fill: COLORS.Pie[pieData.length % COLORS.Pie.length],
    });
  }

  return pieData;
}

function PieChart(props: any) {
  const [pieData, setPieData] = useState<any[]>([]);
  const fieldKey = props.groups[0];
  const countKey = props.sums[0];
  const totalCount = props.totalData?.[countKey] ?? 0;

  useEffect(() => {
    setPieData(normalizeData(props.data, totalCount, countKey, fieldKey));
  }, [props.data, totalCount, countKey, fieldKey]);

  if (!pieData.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  // Filter out entry used to show gray circle when total is 0
  const legendData = pieData
    .filter(({ name }) => !isNil(name))
    .map(({ name, fill }) => ({ value: name, color: fill }));

  return (
    <div style={{ width: "100%", height: 250 }}>
      <ResponsiveContainer height="100%" width="100%">
        <RePieChart>
          <Legend
            payload={legendData}
            formatter={(val) => (
              <span className="legend-label">{Formatter.startCaseWithSpecialChars(val)}</span>
            )}
          />
          {totalCount ? Tooltip : null}

          <Pie data={pieData} nameKey="name" dataKey="count" minAngle={totalCount && 5} legendType="circle" cx="50%"
            cy="50%" outerRadius={80}>
          </Pie>
        </RePieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PieChart;
